import { useState, useEffect, useRef } from 'react';
import './style.css';

const AccordionMultipleSelectItem = (props) => {
  // Auto-expand when there's an active search
  const [isExpanded, setIsExpanded] = useState(false);

  useEffect(() => {
    // Auto-expand when searching and there are matching children
    if (props.searchQuery && props.content.length > 0) {
      setIsExpanded(true);
    } else if (!props.searchQuery) {
      setIsExpanded(false);
    }
  }, [props.searchQuery, props.content.length]);

  const handleToggle = () => {
    setIsExpanded(!isExpanded);
  };

  const handleParentClick = () => {
    props.selectItem(props.content.map(e => e.building_id));
  };

  const handleChildClick = (e) => {
    props.selectItem([e[props.idAttr]]);
  };

  const allChildrenSelected = props.content.every((item) =>
    props.selectedList.map(e => String(e)).includes(String(item[props.idAttr]))
  );

  return (
    <div className={`accordion-item`}>
      <div className={`flex flex-row flex-nowrap items-center justify-start cursor-pointer px-2`}>
        <input
          type="checkbox"
          checked={allChildrenSelected}
          onChange={handleParentClick}
          className="inline-block mr-2 cursor-pointer"
        />
        <button
          className="flex flex-nowrap items-center text-left py-2 w-[full] tablet:text-sm mobile:text-xs w-full justify-between"
          onClick={handleToggle}
        >
          {props.title}
          <svg
            className={`ml-2`}
            transform={`${isExpanded ? 'rotate(90)' : 'rotate(-90)'}`}
            width="8"
            height="16"
            viewBox="0 0 13 22"
            fill="#000000"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path d="M11.9683 1.30177C11.7987 1.13182 11.5972 0.996979 11.3754 0.90498C11.1536 0.81298 10.9159 0.765625 10.6758 0.765625C10.4357 0.765625 10.1979 0.81298 9.97613 0.90498C9.75435 0.996979 9.55288 1.13182 9.38328 1.30177L0.968277 9.71677C0.79832 9.88638 0.663483 10.0878 0.571484 10.3096C0.479484 10.5314 0.432129 10.7692 0.432129 11.0093C0.432129 11.2494 0.479484 11.4871 0.571484 11.7089C0.663483 11.9307 0.79832 12.1322 0.968277 12.3018L9.38328 20.7168C9.55301 20.8865 9.75451 21.0211 9.97628 21.113C10.198 21.2049 10.4357 21.2521 10.6758 21.2521C10.9158 21.2521 11.1535 21.2049 11.3753 21.113C11.597 21.0211 11.7985 20.8865 11.9683 20.7168C12.138 20.547 12.2726 20.3455 12.3645 20.1238C12.4564 19.902 12.5036 19.6643 12.5036 19.4243C12.5036 19.1842 12.4564 18.9465 12.3645 18.7248C12.2726 18.503 12.138 18.3015 11.9683 18.1318L4.85494 11.0001L11.9683 3.88677C12.6833 3.17177 12.6649 1.99844 11.9683 1.30177Z" fill="#000000" />
          </svg>
        </button>
      </div>

      {props.content.sort((a, b) =>
        String(a[props.valAttr]).localeCompare(
          String(b[props.valAttr]),
          undefined,
          { sensitivity: "base" }
        )
      ).map((e, ix) => (
        isExpanded && (
          <div key={ix + "buildingid"} className='flex flex-row p-1 pl-4' onClick={() => handleChildClick(e)}>
            <input
              type="checkbox"
              checked={props.selectedList.map(e => String(e)).includes(String(e[props.idAttr]))}
              onChange={() => { }}
              className="inline-block mr-2 cursor-pointer"
            />
            <p className="text-ellipsis overflow-hidden">
              {e[props.valAttr]}
            </p>
          </div>
        )
      ))}
    </div>
  );
};

const AccordionMultipleSelect = (props) => {
  const [clients, setClients] = useState(null);
  const [show, setShow] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const searchInputRef = useRef(null);

  useEffect(()=>{
    setClients(props.data)
  }, [props.data])

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (show && !event.target.closest('.multi-select-accordion')) {
        setShow(false);
      }
    };
    document.addEventListener('click', handleClickOutside);
    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, [show]);

  // Focus search input when dropdown opens
  useEffect(() => {
    if (show && searchInputRef.current) {
      setTimeout(() => searchInputRef.current?.focus(), 50);
    }
    if (!show) {
      setSearchQuery('');
    }
  }, [show]);

  const selectItem = (e) => {
    props.selectItem(e);
  };

  // Filter clients/children based on search query
  const getFilteredClients = () => {
    if (!clients) return [];
    if (!searchQuery.trim()) return clients;

    const q = searchQuery.toLowerCase();

    return clients
      .map(client => {
        const parentMatches = client.title.toLowerCase().includes(q);
        const matchingChildren = client.content.filter(child =>
          String(child[props.valAttr] ?? '').toLowerCase().includes(q)
        );

        if (parentMatches) {
          // Show parent with all its children when parent name matches
          return { ...client };
        } else if (matchingChildren.length > 0) {
          // Show parent with only matching children
          return { ...client, content: matchingChildren };
        }
        return null;
      })
      .filter(Boolean);
  };

  const filteredClients = getFilteredClients();

  return (
    <div className={`flex flex-nowrap ${props.reports ? 'justify-start' : 'justify-center'} items-center tablet:items-end gap-2.5 rounded-md bg-[#FFF] multi-select-accordion`}>
      <div className={`relative h-10 flex ${props.reports ? 'justify-start' : 'laptop:justify-center laptop:items-start tablet:items-end'} flex-col`}>
        {clients && clients.length > 0 && (
          <div className="relative flex items-center h-[36px] w-[320px] justify-start border border-1 rounded transparent border-[#C1C1C1] text-sm text-normal text-[grey] cursor-pointer">
            <div onClick={() => setShow(!show)} className="w-full mx-3 flex flex-nowrap items-center justify-center">
              <div className={`w-full text-base text-[grey]`}>{props.placeholder}</div>
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" className={`relative inline-block w-3.5 h-3.5 transform transition duration-150 ${show ? "rotate-180" : ""}`}>
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
              </svg>
            </div>

            {show && (
              <div className={`absolute top-[100%] w-full flex flex-col border border-[#C1C1C1] z-10 rounded bg-[#FFF]`}>
                {/* Search box */}
                <div className="flex items-center px-2 py-1.5 border-b border-[#E5E5E5] sticky top-0 bg-[#FFF]">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="w-3.5 h-3.5 text-[#999] mr-1.5 shrink-0"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-4.35-4.35M17 11A6 6 0 1 1 5 11a6 6 0 0 1 12 0z" />
                  </svg>
                  <input
                    ref={searchInputRef}
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    onClick={(e) => e.stopPropagation()}
                    placeholder="Search..."
                    className="w-full text-sm text-[#333] placeholder-[#bbb] outline-none bg-transparent"
                  />
                  {searchQuery && (
                    <button
                      onClick={(e) => { e.stopPropagation(); setSearchQuery(''); }}
                      className="text-[#999] hover:text-[#555] ml-1 shrink-0 text-base leading-none"
                    >
                      ×
                    </button>
                  )}
                </div>

                {/* Results list */}
                <div className="h-[250px] overflow-auto flex flex-col p-1 whitespace-nowrap">
                  {filteredClients.length > 0 ? (
                    filteredClients.map((item, index) => (
                      <AccordionMultipleSelectItem
                        compact={props.compact}
                        key={index}
                        title={item.title}
                        content={item.content}
                        index={index}
                        selectedList={props.selectedList}
                        selectItem={selectItem}
                        dataType={props.dataType}
                        idAttr={props.idAttr}
                        valAttr={props.valAttr}
                        searchQuery={searchQuery}
                      />
                    ))
                  ) : (
                    <div className="flex items-center justify-center h-full text-sm text-[#999]">
                      No results found
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default AccordionMultipleSelect;